import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useNavigate } from "react-router-dom";
import { FileText, ClockCounterClockwise, CheckCircle, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import WeeklyImprovementCard from "@/components/WeeklyImprovementCard";
import NotesTodayCard from "@/components/NotesTodayCard";
import CrmActivityCard from "@/components/CrmActivityCard";
import TeamLeaderboardCard from "@/components/TeamLeaderboardCard";

const TONE_ACTIVE = {
  slate: "border-slate-900 bg-slate-900 text-white",
  amber: "border-amber-500 bg-amber-500 text-white",
  emerald: "border-emerald-600 bg-emerald-600 text-white",
  blue: "border-blue-600 bg-blue-600 text-white",
};

const StatCard = ({ label, value, icon: Icon, tone = "slate", onClick, testid }) => (
  <button type="button" onClick={onClick} data-testid={testid || `stat-${label.toLowerCase().replace(/\s/g,'-')}`}
    className={`text-left border p-5 transition-colors duration-150 group bg-white border-slate-200 hover:border-slate-900`}>
    <div className="flex items-start justify-between">
      <div>
        <div className="label-micro">{label}</div>
        <div className="mt-2 font-display text-3xl font-black tabular-nums text-slate-900">{value}</div>
      </div>
      <Icon size={22} className={`text-${tone}-500`} />
    </div>
    <div className="mt-2 text-[10px] font-semibold uppercase tracking-wider text-slate-400 group-hover:text-slate-700">
      View {label.toLowerCase()} →
    </div>
  </button>
);

export default function DashboardPage() {
  const [stats, setStats] = useState({ total: 0, by_status: {} });
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/dashboard/stats");
        setStats(s.data);
      } catch (e) { toast.error("Failed to load dashboard"); }
    })();
  }, []);

  const jumpTo = (status) => nav(status ? `/proposals?status=${status}` : "/proposals");

  return (
    <div className="p-3 sm:p-6 lg:p-8 max-w-[1400px] mx-auto" data-testid="dashboard-page">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="label-micro">Overview</div>
          <h1 className="font-display text-xl sm:text-3xl font-black mt-1">Dashboard</h1>
        </div>
      </div>

      <div className="mt-4 sm:mt-6" data-testid="dashboard-weekly-tracker-slot">
        <WeeklyImprovementCard />
      </div>

      <div className="mt-4 sm:mt-6" data-testid="dashboard-crm-activity-slot">
        <CrmActivityCard />
      </div>

      <div className="mt-4 sm:mt-6" data-testid="dashboard-notes-slot">
        <NotesTodayCard />
      </div>

      <div className="mt-4 sm:mt-6" data-testid="dashboard-leaderboard-slot">
        <TeamLeaderboardCard />
      </div>

      <div className="mt-6 sm:mt-8">
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-display font-bold text-slate-900 text-sm sm:text-base">Proposal summary</h2>
          <button onClick={() => nav("/proposals")} data-testid="dashboard-view-all-proposals" className="text-xs font-semibold text-blue-700 hover:underline">
            View all →
          </button>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2.5 sm:gap-4">
          <StatCard label="Total" value={stats.total || 0} icon={FileText} tone="slate" onClick={() => jumpTo("")} />
          <StatCard label="Draft" value={stats.by_status?.draft || 0} icon={FileText} tone="slate" onClick={() => jumpTo("draft")} />
          <StatCard label="Pending" value={stats.by_status?.pending_approval || 0} icon={ClockCounterClockwise} tone="amber" onClick={() => jumpTo("pending_approval")} />
          <StatCard label="Approved" value={stats.by_status?.approved || 0} icon={CheckCircle} tone="emerald" onClick={() => jumpTo("approved")} />
          <StatCard label="Issued" value={stats.by_status?.issued || 0} icon={PaperPlaneTilt} tone="blue" onClick={() => jumpTo("issued")} />
        </div>
      </div>
    </div>
  );
}
